import { useEffect, useState } from "react";
import { AlertTriangle, X, ArrowRightLeft, Trash2 } from "lucide-react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_LABEL = {
    artist: "Artist",
    venue: "Venue",
    promoter: "Promoter",
    label: "Label",
};

/**
 * Admin-side destructive delete modal.
 *
 * Flow:
 *   1. Load `deletion-preview` for the target user.
 *   2. For every sole-owner workspace the admin must explicitly choose:
 *        • Transfer ownership (inline: new-owner email → POST /transfer-ownership → refresh preview)
 *        • Delete this workspace too (row-level opt-in checkbox)
 *   3. Admin must type the account's email exactly.
 *   4. Submit → DELETE /api/admin/users/{id} with the opted-in workspace ids.
 */
export default function DeleteUserModal({ user, onClose, onDeleted }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [typedEmail, setTypedEmail] = useState("");
    const [wsToDelete, setWsToDelete] = useState(() => new Set());
    const [busy, setBusy] = useState(false);
    const [transferState, setTransferState] = useState({}); // { [orgId]: { email, busy } }

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const d = await endpoints.adminUserDeletionPreview(user.id);
            setPreview(d);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    const soleOwner = preview?.sole_owner_workspaces || [];
    const impact = preview?.impact || {};
    const memberships = preview?.memberships || [];

    const allOwnersHandled = soleOwner.every((w) => wsToDelete.has(w.id));
    const emailMatches = typedEmail.trim().toLowerCase() === (user.email || "").trim().toLowerCase();
    const canSubmit = !loading && !busy && allOwnersHandled && emailMatches;

    const toggleWs = (id) => {
        setWsToDelete((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const runTransfer = async (w) => {
        const st = transferState[w.id] || {};
        const email = (st.email || "").trim();
        if (!email) {
            toast.error("Enter the new owner's Project Mint email");
            return;
        }
        setTransferState((s) => ({ ...s, [w.id]: { ...st, busy: true } }));
        try {
            await endpoints.adminTransferOwnership({
                kind: w.kind,
                org_id: w.id,
                from_user_id: user.id,
                new_owner_email: email,
            });
            toast.success(`${KIND_LABEL[w.kind]} ownership transferred`);
            setTransferState((s) => ({ ...s, [w.id]: { email: "", busy: false } }));
            // Refresh preview so this workspace disappears from sole-owner list
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
            setTransferState((s) => ({ ...s, [w.id]: { ...st, busy: false } }));
        }
    };

    const doDelete = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setError("");
        try {
            await endpoints.adminDeleteUser(user.id, {
                confirm_email: typedEmail.trim(),
                delete_workspace_ids: Array.from(wsToDelete),
            });
            toast.success("Account deleted");
            onDeleted?.();
            onClose?.();
        } catch (err) {
            setError(formatApiError(err));
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            data-testid="admin-delete-user-modal"
        >
            <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-[#ff3333] bg-[#0a0a0a]">
                <header className="flex items-start justify-between p-5 border-b border-[#2a2a2a]">
                    <div>
                        <div className="flex items-center gap-2 text-[#ff3333]">
                            <AlertTriangle className="w-4 h-4" />
                            <div className="text-[10px] font-mono uppercase tracking-widest">Destructive action</div>
                        </div>
                        <h2 className="mt-1 font-display font-black uppercase tracking-tighter text-2xl">Delete account</h2>
                        <div className="mt-1 text-xs font-mono uppercase text-[#a3a3a3] break-all">{user.email}</div>
                    </div>
                    <button
                        onClick={onClose}
                        className="pm-btn text-[10px]"
                        data-testid="admin-delete-modal-close"
                        aria-label="Close"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </header>

                <div className="p-5 space-y-5">
                    {loading && (
                        <div className="text-xs font-mono uppercase text-[#666]" data-testid="admin-delete-preview-loading">
                            Loading impact…
                        </div>
                    )}

                    {!loading && preview && (
                        <>
                            <section className="border border-[#2a2a2a] p-4">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Impact</div>
                                <ul className="mt-2 space-y-1 text-[11px] font-mono uppercase text-[#a3a3a3]">
                                    <li data-testid="impact-registrations">Event registrations to delete: <span className="text-[#f4f4f0]">{impact.registrations}</span></li>
                                    <li data-testid="impact-artist-follows">Artist follows to remove: <span className="text-[#f4f4f0]">{impact.artist_follows}</span></li>
                                    <li data-testid="impact-venue-follows">Venue follows to remove: <span className="text-[#f4f4f0]">{impact.venue_follows}</span></li>
                                    <li>Workspace memberships to remove: <span className="text-[#f4f4f0]" data-testid="impact-memberships">{memberships.length}</span></li>
                                    <li>Pending access requests to clear: <span className="text-[#f4f4f0]">{impact.pending_access_requests}</span></li>
                                    <li>Pending invitations to this email to clear: <span className="text-[#f4f4f0]">{impact.pending_invitations_to_me}</span></li>
                                </ul>
                            </section>

                            {soleOwner.length > 0 && (
                                <section className="border border-[#ff9500] p-4" data-testid="sole-owner-section">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff9500] flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" /> Sole-owner workspaces
                                    </div>
                                    <div className="mt-1 text-[11px] font-mono text-[#a3a3a3]">
                                        This account is the ONLY Owner of the workspaces below. Choose an outcome for each before deleting.
                                    </div>
                                    <ul className="mt-3 space-y-3">
                                        {soleOwner.map((w) => {
                                            const st = transferState[w.id] || {};
                                            const willDelete = wsToDelete.has(w.id);
                                            return (
                                                <li
                                                    key={w.id}
                                                    className="border border-[#2a2a2a] p-3"
                                                    data-testid={`sole-owner-row-${w.id}`}
                                                >
                                                    <div className="flex flex-wrap items-center gap-2 justify-between">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-display font-bold uppercase truncate">{w.name}</div>
                                                            <div className="text-[10px] font-mono uppercase text-[#666]">{KIND_LABEL[w.kind]}</div>
                                                        </div>
                                                        <label className="flex items-center gap-2 text-[10px] font-mono uppercase text-[#ff3333] cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={willDelete}
                                                                onChange={() => toggleWs(w.id)}
                                                                data-testid={`delete-ws-checkbox-${w.id}`}
                                                            />
                                                            Delete this {KIND_LABEL[w.kind]} too
                                                        </label>
                                                    </div>
                                                    {!willDelete && (
                                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                                            <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-[#666] mr-1">
                                                                <ArrowRightLeft className="w-3 h-3" /> Transfer ownership
                                                            </div>
                                                            <input
                                                                type="email"
                                                                placeholder="new-owner@email"
                                                                value={st.email || ""}
                                                                onChange={(e) => setTransferState((s) => ({ ...s, [w.id]: { ...st, email: e.target.value } }))}
                                                                className="flex-1 min-w-[200px] bg-transparent border border-[#2a2a2a] px-2 py-1 text-[11px] font-mono"
                                                                data-testid={`transfer-email-input-${w.id}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                disabled={st.busy}
                                                                onClick={() => runTransfer(w)}
                                                                className="pm-btn text-[10px]"
                                                                data-testid={`transfer-submit-${w.id}`}
                                                            >
                                                                {st.busy ? "Transferring…" : "Transfer"}
                                                            </button>
                                                        </div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </section>
                            )}

                            <section className="border border-[#2a2a2a] p-4">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Confirm</div>
                                <p className="mt-2 text-xs text-[#f4f4f0]">
                                    Delete <span className="font-mono">{user.email}</span>? This action cannot be undone. Type the email to confirm:
                                </p>
                                <input
                                    type="text"
                                    value={typedEmail}
                                    onChange={(e) => setTypedEmail(e.target.value)}
                                    placeholder={user.email}
                                    className="w-full mt-3 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff3333] py-2 outline-none font-mono text-sm"
                                    data-testid="admin-delete-email-input"
                                    autoComplete="off"
                                />
                                {!emailMatches && typedEmail.length > 0 && (
                                    <div className="mt-1 text-[10px] font-mono uppercase text-[#ff9500]" data-testid="admin-delete-email-mismatch">
                                        Email does not match.
                                    </div>
                                )}
                                {!allOwnersHandled && (
                                    <div className="mt-2 text-[10px] font-mono uppercase text-[#ff9500]" data-testid="admin-delete-owners-unhandled">
                                        Each sole-owner workspace above must be transferred or explicitly marked to delete.
                                    </div>
                                )}
                            </section>

                            {error && (
                                <div className="text-xs font-mono uppercase text-[#ff3333]" data-testid="admin-delete-error">
                                    {error}
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-2">
                                <button
                                    onClick={onClose}
                                    className="pm-btn text-xs"
                                    data-testid="admin-delete-cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={doDelete}
                                    disabled={!canSubmit}
                                    className="pm-btn text-xs bg-[#ff3333] text-[#0a0a0a] border-[#ff3333] disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="admin-delete-confirm-btn"
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> {busy ? "Deleting…" : "Delete account"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
